import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from './index.module.scss';

interface LanguageDropdownProps {
  currentLanguage: string;
  languageMap?: Record<string, string>;
  nationLogoMap: Record<string, string>;
  onChangeLanguage: (lang: string) => void;
  theme?: 'light' | 'dark';
  className?: string;
  hideCurrentInMenu?: boolean;
}

const LanguageDropdown: React.FC<LanguageDropdownProps> = ({
  currentLanguage,
  languageMap = {},
  nationLogoMap,
  onChangeLanguage,
  theme = 'light',
  className = '',
  hideCurrentInMenu = false,
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const closeTimerRef = useRef<NodeJS.Timeout | null>(null);

  const languageEntries = useMemo(
    () =>
      Object.entries(languageMap)
        .filter(([lang, label]) => !!label && (!hideCurrentInMenu || lang !== currentLanguage))
        .reverse(),
    [currentLanguage, hideCurrentInMenu, languageMap]
  );

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  const openMenu = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setShowMenu(true);
  };

  const closeMenu = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
    }
    closeTimerRef.current = setTimeout(() => {
      setShowMenu(false);
    }, 120);
  };

  return (
    <div
      className={`${styles.dropdown} ${styles[`dropdown_${theme}`]} ${showMenu ? styles.dropdownOpen : ''} ${className}`.trim()}
      onMouseEnter={openMenu}
      onMouseLeave={closeMenu}
    >
      <span className={styles.currentLabel}>
        {nationLogoMap[currentLanguage] && (
          <img className={styles.flagImage} src={nationLogoMap[currentLanguage]} alt="" />
        )}
        <span>{languageMap[currentLanguage]}</span>
      </span>
      <span className={`iconFont icon-expand ${styles.arrow}`}></span>
      {showMenu && languageEntries.length > 0 && (
        <ul
          className={`${styles.menu} ${styles[`menu_${theme}`]}`}
          onMouseEnter={openMenu}
          onMouseLeave={closeMenu}
        >
          {languageEntries.map(([lang, label]) => (
            <li
              key={lang}
              className={lang === currentLanguage ? styles.menuItemActive : ''}
              onClick={() => {
                setShowMenu(false);
                onChangeLanguage(lang);
              }}
            >
              {nationLogoMap[lang] && (
                <img className={styles.flagImage} src={nationLogoMap[lang]} alt="" loading="lazy" />
              )}
              <span>{label}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default LanguageDropdown;
